// =============================================================================
// CONNECTION TYPES - 6 types covering ~95% of real-world integrations
// =============================================================================

export enum ConnectionType {
  OAUTH = 'oauth',                   // User-based OAuth2
  OAUTH_MACHINE = 'oauth_machine',   // Service accounts / service principals
  API_KEY = 'api_key',               // Static API tokens
  BASIC_AUTH = 'basic_auth',         // Username + password (HTTP Basic Auth)
  DATABASE = 'database',             // MySQL, Postgres, MongoDB
  SECRET = 'secret'                  // Generic vault (JSON, certs, keys)
}

// =============================================================================
// CONNECTOR DEFINITIONS - Provider catalog
// =============================================================================

export enum ConnectorStatus {
  AVAILABLE = 'available',
  COMING_SOON = 'coming_soon',
  DEPRECATED = 'deprecated'
}

/**
 * Every category value used by `connector-definitions.ts`.
 *
 * This declared only 10 of the 17 in use, so any UI that built its list from the type silently
 * dropped the other 7 — Google Docs, Slides, GitHub, Jira, Calendar, Outlook, Notion, Zendesk,
 * Mailchimp, OpenWeather and Google Maps, 11 connectors in total. Keep this in step with the
 * definitions file; it is the source both the connectors page and onboarding read.
 */
export type ConnectorCategory =
  | 'database'
  | 'spreadsheet'
  | 'document'
  | 'analytics'
  | 'crm'
  | 'payments'
  | 'cloud'
  | 'ai'
  | 'storage'
  | 'communication'
  | 'productivity'
  | 'development'
  | 'support'
  | 'marketing'
  | 'ecommerce'
  | 'weather'
  | 'maps';

/**
 * Display labels, ordered as a business owner would look for them rather than alphabetically:
 * the biggest and most-asked-for groups first.
 */
export const CONNECTOR_CATEGORY_LABELS: Record<ConnectorCategory, string> = {
  database: 'Databases',
  spreadsheet: 'Spreadsheets',
  analytics: 'Analytics',
  crm: 'Customers & CRM',
  payments: 'Payments',
  communication: 'Messaging & email',
  productivity: 'Calendars & notes',
  document: 'Documents',
  ecommerce: 'Online stores',
  support: 'Support',
  marketing: 'Marketing',
  development: 'Developer tools',
  ai: 'AI',
  cloud: 'Cloud',
  storage: 'Files & storage',
  weather: 'Weather',
  maps: 'Maps',
};

/** Category order for menus and pickers. */
export const CONNECTOR_CATEGORY_ORDER = Object.keys(
  CONNECTOR_CATEGORY_LABELS,
) as ConnectorCategory[];

// =============================================================================
// UI SCHEMA - Schema-driven form rendering
// =============================================================================

export const UI_FIELD_TYPES = {
  TEXT: 'text',
  PASSWORD: 'password',
  TEXTAREA: 'textarea',
  SELECT: 'select',
  JSON: 'json',
  UPLOAD: 'upload',
  NUMBER: 'number',
  CHECKBOX: 'checkbox',
  RESOURCE_PICKER: 'resource_picker',
} as const;

export type UIFieldType = (typeof UI_FIELD_TYPES)[keyof typeof UI_FIELD_TYPES];

// =============================================================================
// RESOURCE PICKER - Native file/resource picker configuration
// =============================================================================

export type ResourcePickerProvider = 'google' | 'microsoft' | 'dropbox';

export interface ResourcePickerConfig {
  provider: ResourcePickerProvider;
  resourceType: 'spreadsheet' | 'document' | 'presentation' | 'folder' | 'any';
  multiple?: boolean;
  maxSelection?: number;
  google?: {
    viewId?: 'SPREADSHEETS' | 'DOCS' | 'PRESENTATIONS' | 'FOLDERS';
    mimeTypes?: string[];
    appId?: string;        // Google Cloud Project Number - required for drive.file scope
  };
  microsoft?: {
    filters?: string[]; // e.g., ['.xlsx', '.xls']
  };
}

export interface SelectedResource {
  id: string;
  name: string;
  url?: string;
  mimeType?: string;
  provider: ResourcePickerProvider;
  metadata?: Record<string, unknown>;
}

export interface UIField {
  name: string;
  label: string;
  type: UIFieldType;
  required: boolean;
  placeholder?: string;
  description?: string;
  defaultValue?: string | number | boolean;
  options?: Array<{ value: string; label: string }>; // For select type
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
  };
  pickerConfig?: ResourcePickerConfig; // For resource_picker type
}

export interface AuthMethod {
  id: string;                        // 'oauth2', 'service_account', 'uri', 'fields'
  label: string;                     // Display label
  description: string;               // Help text
  fields?: UIField[];                // Required for non-OAuth methods
  helpText?: string;                 // Additional instructions
  resourceScope?: 'all' | 'selected'; // Resource access scope (default: 'all')
  selectedScopeOverrides?: string[]; // OAuth scopes when resourceScope is 'selected'
}

export interface UISchema {
  authMethods?: AuthMethod[];        // Multiple auth methods (e.g., OAuth + Service Account)
  fields?: UIField[];                // Direct fields (for single auth method)
  helpText?: string;
  helpUrl?: string;
}

// =============================================================================
// OAUTH CONFIG - OAuth2 provider configuration
// =============================================================================

export interface PKCEConfig {
  required: boolean;                 // true = always use PKCE
  method: 'S256' | 'plain';          // S256 recommended
}

export interface UserInfoConfig {
  url: string;                       // e.g., 'https://www.googleapis.com/oauth2/v2/userinfo'
  tokenInUrl?: boolean;              // If true, replace {token} in URL (e.g., HubSpot)
  mapping: {
    id: string;                      // Path to unique account ID (e.g., 'id', 'user.id')
    email?: string;                  // Path to email (e.g., 'email', 'user.email')
    name?: string;                   // Path to display name
    avatar?: string;                 // Path to avatar URL (e.g., 'picture', 'avatar_url')
  };
  scopesPath?: string;              // Path to the granted-scopes array in the response
                                    // (e.g., 'scopes' for HubSpot's /oauth/v1/access-tokens/{token}).
                                    // Captured so the product can gate use cases by what the
                                    // account actually granted (optional scopes vary per account).
}

export interface OAuthConfig {
  clientIdEnvVar: string;            // e.g., 'GOOGLE_CLIENT_ID'
  clientSecretEnvVar: string;        // e.g., 'GOOGLE_CLIENT_SECRET'
  redirectUriEnvVar?: string;        // Optional override, defaults to CONNECTION_OAUTH_REDIRECT_URI
  authorizeUrl: string;              // OAuth authorization endpoint
  tokenUrl: string;                  // OAuth token endpoint
  scopes: string[];                  // Required OAuth scopes (sent in `scope` — must be granted or consent fails)
  optionalScopes?: string[];         // Optional OAuth scopes (sent in `optional_scope`). Granted only if
                                     // the account/user has the permission/Hub; excluded otherwise WITHOUT
                                     // failing the install. Lets one connector cover account-dependent
                                     // capabilities (e.g. HubSpot tickets/marketing) — see connector.types
                                     // UserInfoConfig.scopesPath for reading back what was granted.
  additionalParams?: Record<string, string>; // Extra params for authorize URL (e.g., access_type: 'offline')
  tokenExchangeParams?: Record<string, string>; // Extra params for token exchange POST body (e.g., expiring: '1')
  pkce?: PKCEConfig;                 // PKCE configuration for providers requiring it
  userInfoConfig?: UserInfoConfig;   // Config for fetching user info after OAuth
}

// =============================================================================
// CONNECTOR BRAND THEME - Provider-specific button styling
// =============================================================================

export interface ConnectorBrandTheme {
  primary: string;            // Main brand color (hex), e.g., '#0078D4' for Microsoft
  primaryForeground?: string; // Text color on primary (default: white)
  hover?: string;             // Hover state color (default: primary at 90% opacity)
}

// =============================================================================
// CONNECTOR ENTITY - Provider definition stored in database
// =============================================================================

export interface Connector {
  id: string;                        // e.g., 'google-sheets', 'postgresql'
  name: string;                      // e.g., 'Google Sheets'
  description?: string;
  icon: string;                      // Lucide icon name, e.g., 'FileSpreadsheet' (fallback)
  iconUrl?: string;                  // Custom logo URL (priority over icon)
  category: ConnectorCategory;
  connectionType: ConnectionType;
  status: ConnectorStatus;
  isPublic: boolean;
  isFeatured: boolean;
  isPopular: boolean;
  isBeta: boolean;
  displayOrder: number;
  tags: string[];
  version: string;
  uiSchema: UISchema;
  oauthConfig?: OAuthConfig;         // Only for OAuth2 types
  brandTheme?: ConnectorBrandTheme;  // Provider-specific button styling
  createdAt: Date;
  updatedAt: Date;
}

export interface ConnectorListItem {
  id: string;
  name: string;
  description?: string;
  icon: string;
  iconUrl?: string;
  category: ConnectorCategory;
  connectionType: ConnectionType;
  status: ConnectorStatus;
  isFeatured: boolean;
  isPopular: boolean;
  isBeta: boolean;
  brandTheme?: ConnectorBrandTheme;
}

// =============================================================================
// CONNECTION ENTITY - User's credential instance
// =============================================================================

export enum ConnectionStatus {
  ACTIVE = 'active',
  ERROR = 'error',
  PENDING = 'pending',
}

export interface TokenData {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: Date;
  tokenType?: string;
  scope?: string;
}

export interface Connection {
  id: string;
  userId: string;
  workspaceId: string;
  type: string;                      // Connector ID - references connector definition (required)
  connectorId?: string;              // Alias for type (used in some contexts for clarity)
  name: string;
  authType: string;                  // Connection type from connector (e.g., 'oauth', 'api_key')
  authMethod?: string;               // Which auth method was used (e.g., 'oauth', 'service_account')
  credentials: Record<string, unknown>; // Encrypted credentials (includes OAuth tokens for OAuth connections)
  tokenData?: TokenData;             // Typed access to OAuth tokens (derived from credentials)
  metadata?: Record<string, unknown>;
  status: ConnectionStatus;
  errorMessage?: string;             // Error message if status is 'error'
  enabled: boolean;
  lastRefreshedAt?: Date;
  tokenExpiresAt?: Date;
  /**
   * Whether this connection actually holds a refresh token, so the UI only offers
   * "Refresh Token" when it can succeed. Several providers issue non-expiring tokens
   * with no refresh token at all (GitHub OAuth Apps, Slack), and static-token auth
   * methods (PAT / API key) never have one. Derived server-side — the token itself is
   * never sent to the client.
   */
  hasRefreshToken?: boolean;
  lastSync?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// =============================================================================
// DTOs - API Request/Response types
// =============================================================================

export interface CreateConnectionDto {
  type: string;                      // Required - connector ID (references connector)
  name: string;
  authMethod?: string;               // Which auth method to use
  credentials: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface UpdateConnectionDto {
  name?: string;
  credentials?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  enabled?: boolean;
}

export interface OAuthInitDto {
  connectorId: string;               // Connector ID
  connectionName?: string;
  authMethod?: string;               // Which auth method to use (for scope overrides)
  scopes?: string[];                 // Override default scopes
  additionalParams?: Record<string, string>; // Pre-OAuth fields (e.g., shopDomain for Shopify)
}

export interface OAuthInitResponse {
  authUrl: string;
  state: string;
}

export interface OAuthExchangeDto {
  code: string;
  state: string;
}

export interface OAuthExchangeResponse {
  connectionId: string;
  connectorId: string;               // Connector ID
  connectorName: string;
  reauthorized?: boolean;            // True if this was a reauthorization flow
}

export interface ServiceAccountUploadDto {
  type: string;                      // Connector ID
  name: string;
  serviceAccountJson: string | Record<string, unknown>;
}

export interface ProxyEndpoint {
  host: string;
  port: number;
}

export interface ConnectionAccessResponse {
  type: string;                      // Auth method (e.g., 'oauth', 'personal_token', 'service_account')
  data: Record<string, unknown>;     // Access token, credentials, etc.
  metadata?: Record<string, unknown>; // Non-sensitive display data (e.g., selectedResources)
  expiresAt?: Date;
  proxyEndpoints?: ProxyEndpoint[];  // Multiple proxy endpoints for HA failover
}

export interface TestConnectionDto {
  connectorId: string;               // Connector ID
  credentials: Record<string, unknown>;
  authMethod?: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

// =============================================================================
// CONNECTION USAGE INSTRUCTIONS - API response for usage documentation
// =============================================================================

export interface ConnectionUsageInstructionsResponse {
  connectionId: string;
  connectionName: string;
  connectorId: string;
  connectorName: string;
  authType: string;
  markdown: string; // Rendered markdown with placeholders replaced
}

// =============================================================================
// CONNECTOR ID - String identifier for connectors (database-driven)
// =============================================================================

/**
 * Connector IDs are strings stored in the `connectors` table.
 * No enum needed - connectors are fully database-driven.
 *
 * Examples: 'google-sheets', 'postgresql', 'slack', 'stripe'
 *
 * Use `connectorId: string` in your code instead of an enum.
 */
export type ConnectorId = string;

